import { useMemo } from 'react';
import { IAccountsPayableOpo } from '@nkr/models/index'
import { ReportWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Button, Label } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';

interface IProps {
    data: IAccountsPayableOpo[],
    currentLabel: string,
    lastLabel: string,
    previousLabel: string,
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onRowDataChanged: (e: RowDataChangedEvent) => void,
    onShowDetail: (opoId: number) => void,
}

const Grid = (props: IProps) => {
    const getTotals = (): IAccountsPayableOpo[] => {
        let totals = {
            opoId: 0,
            opoAlias: '',
            currentSemester: 0,
            lastSemester: 0,
            prevSemester: 0,
            older: 0,
            total: 0,
            payNow: 0,
            hasAch: false,
            achApproved: false,
            centerId: 0,
            currentSemesterLabel: '',
            lastSemesterLabel: '',
            prevSemesterLabel: ''
        };

        props.data.forEach(item => {
            totals.currentSemester += item.currentSemester;
            totals.lastSemester += item.lastSemester;
            totals.prevSemester += item.prevSemester;
            totals.older += item.older;
            totals.total += item.total;
            totals.payNow += item.payNow;
        });

        return [totals];
    };

    const renderValue = (value: number) => {
        if (value == 0) {
            return <>-</>;
        }

        return <>{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            sortable: false,
            width: 50,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            cellRendererFramework: (params: ICellRendererParams) => params.data.opoId > 0 ? <RankRenderer {...params} /> : <></>
        },
        {
            colId: 'opoAlias',
            field: 'opoAlias',
            headerName: 'OPO',
            width: 125,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IAccountsPayableOpo;

                if (data.opoAlias == '') {
                    return (
                        <>Totals</>
                    );
                }

                if (data.centerId > 0) {
                    return (
                        <><a href={'/center/' + data.centerId + '/profile/edit/admin'} target='_blank'>{params.value}</a></>
                    )
                }

                return (
                    <>{params.value}</>
                );
            }
        },
        {
            colId: 'currentSemester',
            field: 'currentSemester',
            headerClass: 'list-header-right',
            headerName: props.currentLabel,
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>{props.currentLabel}</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'lastSemester',
            field: 'lastSemester',
            headerClass: 'list-header-right',
            headerName: props.lastLabel,
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>{props.lastLabel}</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'prevSemester',
            field: 'prevSemester',
            headerClass: 'list-header-right',
            headerName: props.previousLabel,
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>{props.previousLabel}</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'older',
            field: 'older',
            headerClass: 'list-header-right',
            headerName: 'Older',
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Older</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'total',
            field: 'total',
            headerClass: 'list-header-right',
            headerName: 'Total',
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Total</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'payNow',
            field: 'payNow',
            headerClass: 'list-header-right',
            headerName: 'Pay Now',
            width: 60,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pay Now</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },
        {
            colId: 'hasAch',
            field: 'hasAch',
            headerName: 'ACH',
            width: 40,
            sortingOrder: ['desc', 'asc'],
            comparator: (a, b, nodeA, nodeB) => {
                const getVal = (data: IAccountsPayableOpo) => {
                    if (data.hasAch && data.achApproved) return 0;
                    if (data.hasAch && !data.achApproved) return 1;
                    return 2;
                };
                return getVal(nodeA.data) - getVal(nodeB.data);
            },
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ACH Status</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IAccountsPayableOpo;

                if (data.opoAlias == '') {
                    return null;
                }

                if (data.hasAch && data.achApproved) {
                    return <Label bsStyle='success'>ACH Approved</Label>
                }

                if (data.hasAch && !data.achApproved) {
                    return <Label bsStyle='warning'>ACH Not Approved</Label>
                }

                if (!data.hasAch) {
                    return <Label bsStyle='danger'>ACH Missing</Label>
                }
            }
        },
        {
            colId: 'isCenter',
            field: 'isCenter',
            headerName: 'Center',
            width: 40,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.centerId > 0 ? <span className='label label-success'>Y</span> : '-'
        },
        {
            colId: 'details',
            field: 'opoId',
            headerName: 'details',
            sortable: false,
            width: 50,
            sortingOrder: ['desc', 'asc'],
            headerComponentFramework: (params: IHeaderParams) => <></>,
            pinnedRowCellRendererFramework: () => <></>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.value == 0)
                    return '';
                return <Button bsStyle='primary' bsSize='xsmall' onClick={() => props.onShowDetail(params.value)}>Details</Button>
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [props.currentLabel, props.lastLabel, props.previousLabel]);
    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(), [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell-center',
                    sortable: true,
                    cellClassRules: {
                        'totalsBackground': (params) => {
                            return params.data.opoAlias == '';
                        }
                    }
                }}
                rowData={rowData}
                pinnedTopRowData={totals}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                excelStyles={[
                    {
                        id: 'header',
                        alignment: {
                            horizontal: 'Center'
                        },
                        font: {
                            bold: true
                        }
                    },
                    {
                        id: 'cell',
                        alignment: {
                            horizontal: 'Center'
                        }
                    },
                    {
                        id: 'totalsBackground',
                        interior: {
                            color: '#ADD8E6',
                            pattern: 'Solid',
                            patternColor: 'black'
                        }
                    }
                ]}
                onGridReady={props.onGridReady}
                onFirstDataRendered={props.onFirstDataRendered}
                onRowDataChanged={props.onRowDataChanged}
                columnDefs={columnDefs}
                onSortChanged={(e: SortChangedEvent) => {
                    e.api.refreshCells();
                }}
                getRowStyle={(props: any) => {
                    let data = props.data as IAccountsPayableOpo;

                    if (data.opoId == 0) {
                        return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                    }
                }}
            >
            </AgGridReact>
        </ReportWrapper>
    );
}

export default Grid;